import React, { useState } from 'react';
import { GoalsOptionsDTO } from '../api/useGoalsOptionsQuery';
import { StartPageLoading } from '../../BeFi20/Survey/components/StartPage/StartPage.loading';
import { ErrorState } from '../../BeFi20/components/ErrorState';
import { Progress } from '../../CommonComponents/OPL/Progress/Progress';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { SetGoalProvider, WizardState } from './context/SetGoalProvider';
import { getGoalSteps } from './utils/getGoalSteps';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { GoalDescription } from './steps/GoalDescription/GoalDescription';
import { GoalType } from './steps/GoalType/GoalType';
import { Budget } from './steps/Budget/Budget';
import { Accounts } from './steps/Accounts/Accounts';
import { TargetDate } from './steps/TargetDate/TargetDate';
import { Review } from './steps/Review/Review';
import { FORM_ID, StepName } from './SetGoal.meta';
import { useSetGoalMutation } from '../api/useSetGoalMutation';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { Loading } from '../../CommonComponents/OPL/Loading/Loading';
import { Wrapper } from '../PulseCheck.styles';
import { PulseCheckVerticalLogo } from '../img/PulseCheckVerticalLogo';
import { SavedGoalDTO } from '../api/useGoalQuery';
import { dataToWizard } from './utils/dataToWizard';
import { useSetGoal } from './useSetGoal';

interface Props {
  clientId: string;
  categoryId: string;
  goalId: string;
}

export const SetGoal = ({ clientId, categoryId, goalId }: Props) => {
  const { isLoading, isError, refetch, data } = useSetGoal({
    clientId,
    categoryId,
    goalId,
  });

  if (isLoading) {
    return <StartPageLoading logo={<PulseCheckVerticalLogo />} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    throw new Error('No data in PulseCheck set goal');
  }

  return (
    <SetGoalContent
      clientId={clientId}
      categoryId={categoryId}
      options={data.options}
      goalId={goalId || null}
      goal={data.goal || null}
    />
  );
};

const STEP_MAP: Record<StepName, React.FC<{ clientId: string }>> = {
  goalDescription: GoalDescription,
  goalType: GoalType,
  budget: Budget,
  accounts: Accounts,
  targetDate: TargetDate,
  review: Review,
};

interface ContentProps {
  clientId: string;
  categoryId: string;
  options: GoalsOptionsDTO;
  goalId: string | null;
  goal: SavedGoalDTO | null;
}

const SetGoalContent = ({ clientId, categoryId, goalId, options, goal }: ContentProps) => {
  const [wizard, setWizard] = useState<Partial<WizardState>>(goal ? dataToWizard(goal) : {});

  const [step, setStep] = useState<StepName>('goalDescription');

  const [isReviewingStep, setIsReviewingStep] = useState(false);

  const STEPS = getGoalSteps(wizard);

  const stepIndex = STEPS.indexOf(step);

  const { mutateAsync: setGoal, isLoading: isSettingGoal } = useSetGoalMutation();

  const onSuccess = async (_wizard: Partial<WizardState>) => {
    const steps = getGoalSteps(_wizard);

    if (stepIndex === steps.length - 1) {
      try {
        await setGoal({ clientId, category: categoryId, wizard, goalId });

        ADV.util.Router.go2('PulseCheckResults', { clientId });
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        }

        console.error(error);
      }

      return;
    }

    if (isReviewingStep && step === 'goalType') {
      setStep('budget');

      return;
    }

    setStep(isReviewingStep ? 'review' : steps[stepIndex + 1]);
    setIsReviewingStep(false);
  };

  const onPrevious = () => {
    if (stepIndex === 0) {
      ADV.util.Router.go2('PulseCheckResults', { clientId });

      return;
    }

    setStep(STEPS[stepIndex - 1]);
  };

  const Step = STEP_MAP[step];

  if (!Step) {
    throw new Error(`Invalid step ${step}`);
  }

  return (
    <SetGoalProvider value={{ wizard, setWizard, setStep, setIsReviewingStep, onSuccess, options }}>
      <ScrollHereOnce key={step} />

      {isSettingGoal ? (
        <Wrapper css="justify-content: center">
          <Loading text="Setting your goal" />
        </Wrapper>
      ) : (
        <>
          <Progress
            value={((stepIndex + 1) / STEPS.length) * 100}
            isLarge
            isFlat
            css="flex-shrink: 0"
          />

          <Step clientId={clientId} />

          <StickyFooter css="width: 100%; min-height: unset">
            {!isReviewingStep && (
              <Button
                variant="secondary"
                leftIcon="fas fa-chevron-left"
                size="large"
                onClick={onPrevious}
              >
                Previous
              </Button>
            )}

            <Button
              form={FORM_ID}
              type="submit"
              rightIcon="fas fa-chevron-right"
              size="large"
              ml="auto"
            >
              Next
            </Button>
          </StickyFooter>
        </>
      )}
    </SetGoalProvider>
  );
};
